// 261010 Epic 2 / P4b-2 — CHÍNH SÁCH THUẦN của tab "Bối cảnh" (ảnh chuẩn + ảnh theo hướng của location). Không antd / router ⇒ jest chạy được (xem CLAUDE.md của module).
// Quyết HIỂN THỊ / BẬT / LÝ DO của từng chip, nút, hộp xác nhận. Server vẫn chặn thiếu ảnh và job trùng `view:<id>`.
// Enum view = P4a (`EMPTY | GENERATING | ERROR | FRESH | STALE | APPROVED`), KHÔNG trộn với `WorkflowStepState` (`READY`…). KHÔNG hiện giá ở bất cứ chỗ nào (User chốt).
import type {
  CanonicalApprovalState, LocationNeedShot, LocationNeedsResponse, LocationStaleReason, LocationView, LocationViewStatus, LocationViewTextPatch,
} from '../../types/filmLocationAssets';

export type LocActionKey = 'VIEW_GENERATE' | 'VIEW_APPROVE' | 'VIEW_UNAPPROVE' | 'VIEW_EDIT_TEXT' | 'VIEWS_DEFAULTS' | 'VIEW_ADD';
export interface LocAction { key: LocActionKey; label: string; enabled: boolean; reason?: string }
export type ChipTone = 'todo' | 'doing' | 'bad' | 'warn' | 'ready' | 'ok';
export interface Chip { label: string; tone: ChipTone }

const GENERATING_REASON = 'Ảnh hướng này đang tạo — đợi xong.';
const NO_CANON_REASON = 'Chưa có ảnh chuẩn — tạo ảnh chuẩn trên thẻ này trước.';
export const EDIT_DESC_NOTICE = 'Đổi mô tả làm ảnh này thành cũ và gỡ duyệt — cần tạo lại.';
export const DIRECTION_EXISTS_TEXT = 'Mã hướng này đã có ở bối cảnh này — đặt mã khác.';

// ───────────────────────── ảnh chuẩn ─────────────────────────

export interface CanonicalLite { url: string | null; state: CanonicalApprovalState }

/** Ảnh chuẩn có sẵn dùng được ngay; trạng thái pending cũ được ASSETS approve tự duyệt. */
export function canonicalChip(c: CanonicalLite): Chip {
  if (!c.url?.trim()) return { label: 'chưa có', tone: 'todo' };
  return c.state === 'APPROVED' ? { label: 'đã duyệt', tone: 'ok' } : { label: 'có ảnh', tone: 'ok' };
}

// ───────────────────────── ô hướng ─────────────────────────

const VIEW_CHIP: Record<LocationViewStatus, Chip> = {
  EMPTY: { label: 'chưa có', tone: 'todo' },
  GENERATING: { label: 'đang tạo', tone: 'doing' },
  ERROR: { label: 'lỗi lần tạo gần nhất', tone: 'bad' },
  FRESH: { label: 'có, chờ duyệt', tone: 'ready' },
  STALE: { label: 'cũ', tone: 'warn' },
  APPROVED: { label: 'đã duyệt', tone: 'ok' },
};
export const viewChip = (v: Pick<LocationView, 'status'>): Chip => ({ ...(VIEW_CHIP[v.status] ?? VIEW_CHIP.EMPTY) });

/** Đã có ảnh dùng được (FRESH / STALE / APPROVED) ⇒ "Tạo lại"; EMPTY / ERROR ⇒ "Tạo ảnh"; GENERATING theo việc có ảnh cũ hay không. */
const isRegen = (v: Pick<LocationView, 'status' | 'imageUrl'>): boolean =>
  v.status === 'FRESH' || v.status === 'STALE' || v.status === 'APPROVED' || (v.status === 'GENERATING' && !!v.imageUrl);

export function viewActions(v: LocationView, canonical: CanonicalLite): LocAction[] {
  const gen = isRegen(v) ? 'Tạo lại' : 'Tạo ảnh';
  const out: LocAction[] = [];
  if (v.status === 'GENERATING') {
    return [
      { key: 'VIEW_GENERATE', label: gen, enabled: false, reason: GENERATING_REASON },
      { key: 'VIEW_EDIT_TEXT', label: 'Sửa', enabled: false, reason: GENERATING_REASON },
    ];
  }
  // Chỉ thiếu ảnh nguồn mới chặn tạo hướng; không cần duyệt canonical riêng.
  out.push(!!canonical.url?.trim() ? { key: 'VIEW_GENERATE', label: gen, enabled: true } : { key: 'VIEW_GENERATE', label: gen, enabled: false, reason: NO_CANON_REASON });
  if (v.status === 'FRESH') out.push({ key: 'VIEW_APPROVE', label: 'Duyệt', enabled: true });
  if (v.status === 'APPROVED') out.push({ key: 'VIEW_UNAPPROVE', label: 'Bỏ duyệt', enabled: true });
  out.push({ key: 'VIEW_EDIT_TEXT', label: 'Sửa', enabled: true });
  return out;
}

/** Nút cấp location: "Tạo 2 hướng mặc định" chỉ khi chưa có hướng nào (chỉ tạo bản ghi, không gen ảnh); "Thêm hướng" luôn bật. */
export function locationActions(views: LocationView[]): LocAction[] {
  const out: LocAction[] = [];
  if (views.length === 0) out.push({ key: 'VIEWS_DEFAULTS', label: 'Tạo 2 hướng mặc định', enabled: true });
  out.push({ key: 'VIEW_ADD', label: views.length === 0 ? 'Thêm hướng riêng' : 'Thêm hướng', enabled: true });
  return out;
}

// ───────────────────────── lý do cũ / lỗi ─────────────────────────

const REASON_TEXT: Record<LocationStaleReason, string> = {
  CANONICAL_CHANGED: 'ảnh chuẩn đã đổi',
  DIRECTION_CHANGED: 'mô tả hướng đã đổi',
  LOCATION_CHANGED: 'mô tả bối cảnh / thời đại đã đổi',
  WORLD_CHANGED: 'mô tả bối cảnh / thời đại đã đổi',
  TEMPLATE_CHANGED: 'cấu hình tạo ảnh đã đổi',
  MODEL_CHANGED: 'cấu hình tạo ảnh đã đổi',
  SIZE_CHANGED: 'cấu hình tạo ảnh đã đổi',
  INPUT_CHANGED_LEGACY: 'đầu vào đã đổi',
};
const REASON_ORDER: LocationStaleReason[] = [
  'CANONICAL_CHANGED', 'DIRECTION_CHANGED', 'LOCATION_CHANGED', 'WORLD_CHANGED', 'TEMPLATE_CHANGED', 'MODEL_CHANGED', 'SIZE_CHANGED', 'INPUT_CHANGED_LEGACY',
];
/** Nhiều lý do ⇒ nối " · " theo thứ tự cố định, gộp các mã cùng câu. Mã lạ bị bỏ (không lộ mã thô). */
export function staleReasonsText(reasons: LocationStaleReason[] | undefined | null): string {
  const set = new Set(reasons ?? []);
  const texts: string[] = [];
  for (const r of REASON_ORDER) if (set.has(r) && !texts.includes(REASON_TEXT[r])) texts.push(REASON_TEXT[r]);
  return texts.join(' · ');
}

export function errorExcerpt(text: string | null | undefined, max = 140): string {
  const t = String(text ?? '').replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/** Dòng chú dưới chip của ô: STALE ⇒ lý do cũ; ERROR ⇒ `lastError` rút gọn. */
export function viewNote(v: Pick<LocationView, 'status' | 'staleReasons' | 'lastError'>): string {
  if (v.status === 'STALE') return staleReasonsText(v.staleReasons);
  if (v.status === 'ERROR') return errorExcerpt(v.lastError);
  return '';
}

// ───────────────────────── hướng các shot đang cần ─────────────────────────

export const shotLine = (s: Pick<LocationNeedShot, 'eventOrder' | 'shotOrder'>): string => `Video ${s.eventOrder} · shot ${s.shotOrder}`;

export type ShotAssignKind = 'ASSIGNED' | 'ORPHAN' | 'UNASSIGNED';
export interface ShotAssignment { kind: ShotAssignKind; text: string; chip?: Chip; /** true ⇒ dòng viền cam + nút "Thêm hướng này". */ needsView: boolean }

/** `views` = các hướng hiện có của location (null = chưa nạp). Nạp rồi thì nguồn nhãn là view hiện có (tươi hơn `location-needs`). */
export function shotAssignment(shot: LocationNeedShot, views: LocationView[] | null): ShotAssignment {
  const key = shot.storyboardViewKey;
  if (!key) return { kind: 'UNASSIGNED', text: 'chưa gán', needsView: true };
  const v = views ? views.find((x) => x.directionKey === key) : undefined;
  if (views ? !v : shot.storyboardViewLabel === null) return { kind: 'ORPHAN', text: `đã gán mã ${key} — hướng này không còn`, needsView: false };
  const label = v?.label ?? shot.storyboardViewLabel ?? key;
  return { kind: 'ASSIGNED', text: `đã gán trong storyboard: ${label}`, ...(v ? { chip: viewChip(v) } : {}), needsView: false };
}

/** Dòng tóm tắt đầu tab: CHỈ shot "chưa gán" (storyboardViewKey null) trong location; `unresolved` và mã mồ côi không tính. */
export function unassignedSummary(needs: LocationNeedsResponse): { total: number; parts: Array<{ name: string; count: number }>; text: string } {
  const parts = needs.locations
    .map((l) => ({ name: l.name, count: l.shots.filter((s) => !s.storyboardViewKey).length }))
    .filter((p) => p.count > 0);
  const total = parts.reduce((n, p) => n + p.count, 0);
  return { total, parts, text: total === 0 ? 'Mọi shot đã gán ảnh hướng.' : `còn ${total} shot chưa gán ảnh hướng: ${parts.map((p) => `${p.name} (${p.count})`).join(' · ')}` };
}

/** Số shot đã gán vào một hướng ĐÃ DUYỆT (đầu thẻ "x/y shot đã có ảnh hướng duyệt"). `views` null ⇒ 0. */
export function approvedShotCount(shots: LocationNeedShot[], views: LocationView[] | null): number {
  if (!views) return 0;
  const approved = new Set(views.filter((x) => x.status === 'APPROVED').map((x) => x.directionKey));
  return shots.filter((s) => !!s.storyboardViewKey && approved.has(s.storyboardViewKey)).length;
}
export const videosUsing = (shots: Array<Pick<LocationNeedShot, 'eventId'>>): number => new Set(shots.map((s) => s.eventId)).size;

// ───────────────────────── form thêm / sửa hướng ─────────────────────────

/** Cùng luật BE `validateLocationViewDirectionKey`: `^[a-z0-9-]{2,64}$`. FE kiểm trước ⇒ sai thì không gọi API. */
export function validateDirectionKey(raw: string, existingKeys: string[]): string | null {
  const k = String(raw ?? '').trim();
  if (!k) return 'Nhập mã hướng.';
  if (!/^[a-z0-9-]+$/.test(k)) return 'Mã hướng chỉ gồm chữ thường a–z, số và dấu gạch nối.';
  if (k.length < 2 || k.length > 64) return 'Mã hướng dài 2–64 ký tự.';
  if (existingKeys.includes(k)) return DIRECTION_EXISTS_TEXT;
  return null;
}

export interface ViewFormValues { label: string; directionKey: string; directionText: string }
export type ViewFormErrors = Partial<Record<keyof ViewFormValues, string>>;
export function validateViewForm(f: ViewFormValues, existingKeys: string[]): ViewFormErrors {
  const e: ViewFormErrors = {};
  if (!f.label.trim()) e.label = 'Nhập nhãn hướng.';
  const k = validateDirectionKey(f.directionKey, existingKeys); if (k) e.directionKey = k;
  if (!f.directionText.trim()) e.directionText = 'Nhập mô tả hướng.';
  return e;
}

/** "Thêm hướng này" từ một shot: mô tả điền sẵn = vị trí máy + nền (người sửa được); nhãn / mã để trống cho người đặt. */
export function prefillFromShot(shot: Pick<LocationNeedShot, 'cameraPosition' | 'background'>): ViewFormValues {
  const cam = String(shot.cameraPosition ?? '').trim();
  const bg = String(shot.background ?? '').trim();
  const text = cam && bg ? `${/[.!?…]$/.test(cam) ? cam : `${cam}.`} ${bg}` : cam || bg;
  return { label: '', directionKey: '', directionText: text };
}

/** Dòng nhắc trước khi lưu: view ĐÃ có ảnh mà mô tả đổi thật (so sau trim) ⇒ ảnh thành cũ + mất duyệt. Chỉ đổi nhãn ⇒ không nhắc. */
export function editNotice(v: Pick<LocationView, 'imageUrl' | 'directionText'>, newText: string): string | null {
  if (!v.imageUrl) return null;
  return newText.trim() !== String(v.directionText ?? '').trim() ? EDIT_DESC_NOTICE : null;
}

/** Body PATCH chỉ gồm khoá đổi thật. Không đổi gì ⇒ `patch: null` (không gọi API). Trống ⇒ `errors`. */
export function editPatch(v: Pick<LocationView, 'label' | 'directionText'>, f: { label: string; directionText: string }): { patch: LocationViewTextPatch | null; errors?: Partial<Record<'label' | 'directionText', string>> } {
  const errors: Partial<Record<'label' | 'directionText', string>> = {};
  const label = f.label.trim(); const text = f.directionText.trim();
  if (!label) errors.label = 'Nhập nhãn hướng.';
  if (!text) errors.directionText = 'Nhập mô tả hướng.';
  if (errors.label || errors.directionText) return { patch: null, errors };
  const patch: LocationViewTextPatch = {};
  if (label !== String(v.label ?? '').trim()) patch.label = label;
  if (text !== String(v.directionText ?? '').trim()) patch.directionText = text;
  return { patch: Object.keys(patch).length ? patch : null };
}

// ───────────────────────── hộp xác nhận ─────────────────────────

export interface ConfirmText { title: string; body: string; okText: string; cancelText: string }
/** Hộp xác nhận tạo ảnh hướng — KHÔNG giá, chỉ nói rõ lượt này gọi dịch vụ ảnh thật. */
export function generateConfirm(v: Pick<LocationView, 'label' | 'status' | 'imageUrl'>, locationName: string): ConfirmText {
  return {
    title: `${isRegen(v) ? 'Tạo lại' : 'Tạo'} ảnh hướng ${v.label}?`,
    body: `Tạo 1 ảnh dọc 9:16, vẽ theo ảnh chuẩn của ${locationName}. Lượt này gọi dịch vụ tạo ảnh thật; kết quả hiện ở thẻ khi xong.`,
    okText: 'Tạo ảnh',
    cancelText: 'Huỷ',
  };
}
